import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Button, 
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  DataTable,
  toast
} from '@etherisc/ui-kit';
import { quoteService } from '../../services/quoteService';
import { participantPaymentService, type ParticipantPayment } from '../../services/participantPaymentService';
import type { Quote, QuotationAggregates } from '../../types/quote';
import { QUOTE_STATE_LABELS } from '../../types/quote';
import { usePermissions } from '../../hooks/usePermissions';
import {
  ChevronLeftIcon,
  FileTextIcon,
  EditIcon,
  CalendarIcon,
  AlertTriangleIcon,
  BuildingIcon,
  DollarSignIcon,
  ShieldIcon,
} from 'lucide-react';

export function QuoteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [participantPayments, setParticipantPayments] = useState<ParticipantPayment[]>([]);
  const [aggregates, setAggregates] = useState<QuotationAggregates | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchQuote = async () => {
      if (!id) {
        setError('Quote ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await quoteService.getQuoteById(id);
        setQuote(response.quote);
        
        // Load aggregates for accurate total_payable (for payment calculations)
        if (response.quote.id) {
          try {
            const summaryResponse = await quoteService.getQuotationSummary(response.quote.id);
            const aggregatesData = summaryResponse?.quotationResults?.aggregates;
            if (aggregatesData) {
              setAggregates(aggregatesData);
            }
          } catch (error) {
            console.error('Error loading aggregates:', error);
            // Continue without aggregates - will fallback to premiumAmount
          }
        }
        
        // Fetch participant payments for this quote
        if (response.quote.quoteNumber) {
          await fetchParticipantPayments(response.quote.quoteNumber);
        }
      } catch (error) {
        console.error('Error fetching quote:', error);
        setError(error instanceof Error ? error.message : 'Failed to load quote');
        toast.error('Failed to load quote');
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [id]);

  const fetchParticipantPayments = async (quoteNumber: string) => {
    try {
      setPaymentsLoading(true);
      const response = await participantPaymentService.listParticipantPayments({ quoteNumber });
      setParticipantPayments(response.payments);
    } catch (error) {
      console.error('Error fetching participant payments:', error);
      // Don't show error toast - payments might not exist yet
    } finally {
      setPaymentsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading quote...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-600 mb-4">{error}</div>
        <Button onClick={() => navigate('/admin/quotes')}>
          Back to Quotes
        </Button>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-600 mb-4">Quote not found</div>
        <Button onClick={() => navigate('/admin/quotes')}>
          Back to Quotes
        </Button>
      </div>
    );
  }

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString();
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatCents = (cents: number) => {
    return formatCurrency(cents / 100);
  };

  const getCoverageDuration = () => {
    const start = new Date(quote.coverageStartDate);
    const end = new Date(quote.coverageEndDate);
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return `${days} days`;
  };

  const getCutoffDate = () => {
    const start = new Date(quote.coverageStartDate);
    start.setDate(start.getDate() - 1);
    return formatDate(start);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'REJECTED': return 'destructive';
      case 'UNDER_REVIEW': return 'warning';
      case 'DRAFT': return 'secondary';
      default: return 'secondary';
    }
  };

  // Calculate payment totals using aggregates.total.total_payable (not premiumAmount)
  // Formula: Paid + Due = Total Payable (always)
  // Overpaid = excess amount paid above total payable
  const totalPayable = aggregates?.total?.total_payable || Number(quote.premiumAmount || 0);
  const totalPremiumCents = Math.round(totalPayable * 100);
  const totalActualPaidCents = participantPayments.reduce((sum, p) => sum + p.paidAmountCents, 0);
  const totalPaidCents = Math.min(totalActualPaidCents, totalPremiumCents); // Cap at total payable
  const totalOverpaidCents = Math.max(0, totalActualPaidCents - totalPremiumCents); // Excess only
  const totalDueCents = Math.max(0, totalPremiumCents - totalActualPaidCents); // Remaining

  // Enrich participants with payment data
  const participantsWithPayments = quote.participants?.map(participant => {
    const payment = participantPayments.find(p => p.participantId === participant.id);
    
    // Use participant-specific total_payable from aggregates if available
    let premiumCents = 0;
    
    // For individual policies, POLICYHOLDER pays the full premium
    if (!quote.isMasterPolicy && participant.role === 'POLICYHOLDER') {
      premiumCents = totalPremiumCents;
    } else if (aggregates && participant.farmerId) {
      // For master policies or INSURED participants, use participant-specific aggregates
      const participantAggregate = aggregates.participants.find(pa => pa.farmerId === participant.farmerId);
      if (participantAggregate && participantAggregate.total_payable > 0) {
        premiumCents = Math.round(participantAggregate.total_payable * 100);
      } else if (participant.sumInsured && quote.sumInsured && totalPayable > 0) {
        // Fallback: calculate proportionally from total_payable
        premiumCents = Math.round((Number(participant.sumInsured) / Number(quote.sumInsured)) * totalPayable * 100);
      }
    } else if (participant.sumInsured && quote.sumInsured && totalPayable > 0) {
      // Fallback: calculate proportionally from total_payable
      premiumCents = Math.round((Number(participant.sumInsured) / Number(quote.sumInsured)) * totalPayable * 100);
    }
    
    const totalPaidCents = payment?.paidAmountCents || 0;
    
    // Calculate paid vs overpaid
    const paidCents = Math.min(totalPaidCents, premiumCents);
    const overpaidCents = Math.max(0, totalPaidCents - premiumCents);
    const dueCents = Math.max(0, premiumCents - totalPaidCents);
    
    return {
      id: participant.id,
      role: participant.role,
      name: participant.farmer?.name || participant.organization?.name || 'Unknown',
      type: participant.farmer ? 'Farmer' : 'Organization',
      farmerCode: participant.farmer?.farmerCode,
      sumInsured: participant.sumInsured,
      hectaresAnimalCount: participant.hectaresAnimalCount,
      premiumCents,
      paidCents,
      overpaidCents,
      dueCents,
      paymentStatus: payment?.status || 'not_initiated'
    };
  }) || [];

  // Define participants table columns
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const participantColumns: any[] = [
    {
      id: 'role',
      header: 'Role',
      accessorKey: 'role' as const,
      cell: ({ getValue }: any) => (
        <Badge variant="secondary" className="text-xs">
          {getValue()}
        </Badge>
      ),
    },
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name' as const,
      cell: ({ row }: any) => (
        <div>
          <p className="font-medium">{row.original.name}</p>
          {row.original.farmerCode && (
            <p className="text-xs text-gray-500 font-mono">{row.original.farmerCode}</p>
          )}
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      accessorKey: 'type' as const,
    },
    {
      id: 'sumInsured',
      header: 'Sum Insured',
      accessorFn: (row: any) => row.sumInsured,
      cell: ({ getValue, row }: any) => {
        // Hide for POLICYHOLDER only in master policies (they don't pay)
        if (row.original.role === 'POLICYHOLDER' && quote.isMasterPolicy) return null;
        return <div className="text-right">{formatCurrency(getValue() || 0)}</div>;
      },
    },
    {
      id: 'hectaresAnimalCount',
      header: 'Hectares/Animals Insured',
      accessorFn: (row: any) => row.hectaresAnimalCount,
      cell: ({ getValue, row }: any) => {
        // Hide for POLICYHOLDER only in master policies (they don't pay)
        if (row.original.role === 'POLICYHOLDER' && quote.isMasterPolicy) return null;
        const value = getValue();
        return <div className="text-right">{value ? value.toLocaleString() : '-'}</div>;
      },
    },
    {
      id: 'premium',
      header: 'Premium',
      accessorFn: (row: any) => row.premiumCents,
      cell: ({ getValue, row }: any) => {
        // Hide for POLICYHOLDER only in master policies (they don't pay)
        if (row.original.role === 'POLICYHOLDER' && quote.isMasterPolicy) return null;
        return <div className="text-right">{formatCents(getValue())}</div>;
      },
    },
    {
      id: 'paid',
      header: 'Paid',
      accessorFn: (row: any) => row.paidCents,
      cell: ({ getValue, row }: any) => {
        // Hide for POLICYHOLDER only in master policies (they don't pay)
        if (row.original.role === 'POLICYHOLDER' && quote.isMasterPolicy) return null;
        return (
          <div className="text-right">
            <span className="text-green-600 font-medium">{formatCents(getValue())}</span>
          </div>
        );
      },
    },
    {
      id: 'overpaid',
      header: 'Overpaid',
      accessorFn: (row: any) => row.overpaidCents,
      cell: ({ getValue, row }: any) => {
        // Hide for POLICYHOLDER only in master policies (they don't pay)
        if (row.original.role === 'POLICYHOLDER' && quote.isMasterPolicy) return null;
        const overpaid = getValue();
        return overpaid > 0 ? (
          <div className="text-right">
            <span className="text-purple-600 font-medium">{formatCents(overpaid)}</span>
          </div>
        ) : (
          <div className="text-right">
            <span className="text-gray-400">-</span>
          </div>
        );
      },
    },
    {
      id: 'due',
      header: 'Due',
      accessorFn: (row: any) => row.dueCents,
      cell: ({ getValue, row }: any) => {
        // Hide for POLICYHOLDER only in master policies (they don't pay)
        if (row.original.role === 'POLICYHOLDER' && quote.isMasterPolicy) return null;
        const due = getValue();
        return (
          <div className="text-right">
            <span className={due > 0 ? 'text-orange-600 font-medium' : 'text-gray-500'}>
              {formatCents(due)}
            </span>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Payment Status',
      accessorKey: 'paymentStatus' as const,
      cell: ({ getValue, row }: any) => {
        // Hide for POLICYHOLDER only in master policies (they don't pay)
        if (row.original.role === 'POLICYHOLDER' && quote.isMasterPolicy) return null;
        const status = getValue();
        const statusColors: Record<string, string> = {
          completed: 'bg-green-100 text-green-800',
          partial: 'bg-yellow-100 text-yellow-800',
          pending: 'bg-gray-100 text-gray-800',
          not_initiated: 'bg-gray-50 text-gray-500',
          failed: 'bg-red-100 text-red-800',
        };
        return (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || statusColors.not_initiated}`}>
            {status.replace('_', ' ')}
          </span>
        );
      },
    },
  ];
  /* eslint-enable @typescript-eslint/no-explicit-any */

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/quotes')}
            className="flex items-center gap-2"
          >
            <ChevronLeftIcon size={20} />
            Back to Quotes
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileTextIcon size={24} />
              Quote {quote.quoteNumber}
            </h1>
            <p className="text-gray-600 mt-1">
              {quote.templateName || 'Quote Details'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant={getStatusColor(quote.state)}>
            {QUOTE_STATE_LABELS[quote.state as keyof typeof QUOTE_STATE_LABELS] || quote.state}
          </Badge>
          {quote.state === 'new' && hasPermission('quotes:update') && (
            <Button
              variant="outline"
              onClick={() => navigate(`/admin/quotes/${quote.id}/edit`)}
              className="flex items-center gap-2"
            >
              <EditIcon size={16} />
              Edit
            </Button>
          )}
          {/* Edit button intentionally not rendered for approved or later states (#83) */}
        </div>
      </div>

      {/* Top Row: Quote Information and Coverage Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quote Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileTextIcon size={20} />
              Quote Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Quote Number</label>
                <p className="text-sm text-gray-900 font-mono">{quote.quoteNumber}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Status</label>
                <p className="text-sm text-gray-900">{QUOTE_STATE_LABELS[quote.state as keyof typeof QUOTE_STATE_LABELS] || quote.state}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Product</label>
                <p className="text-sm text-gray-900">{quote.product?.name || 'Unknown Product'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Master Policy</label>
                <p className="text-sm text-gray-900">{quote.isMasterPolicy ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Valid Until</label>
                <p className="text-sm text-gray-900">{formatDate(quote.validUntil)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Sum Insured</label>
                <p className="text-sm text-gray-900 font-semibold">{formatCurrency(quote.sumInsured)}</p>
              </div>
              {quote.hectaresAnimalCount && quote.hectaresAnimalCount > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Hectares/Animals Insured</label>
                  <p className="text-sm text-gray-900 font-semibold">{quote.hectaresAnimalCount.toLocaleString()}</p>
                </div>
              )}
            </div>

            {/* Payment Details */}
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 mb-3">
                <DollarSignIcon size={16} className="text-gray-600" />
                <label className="text-sm font-medium text-gray-700">Payment Details</label>
              </div>
              <div className="space-y-3">
                {/* Payment Amounts */}
                <div className="grid grid-cols-2 gap-3 bg-blue-50 rounded-lg p-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600">Total Premium</label>
                    <p className="text-sm text-gray-900 font-bold">{formatCents(totalPremiumCents)}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">Total Paid</label>
                    <p className="text-sm text-green-600 font-bold">{formatCents(totalPaidCents)}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">Total Overpaid</label>
                    <p className="text-sm text-purple-600 font-bold">
                      {totalOverpaidCents > 0 ? formatCents(totalOverpaidCents) : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">Total Due</label>
                    <p className="text-sm text-orange-600 font-bold">{formatCents(totalDueCents)}</p>
                  </div>
                </div>

                {/* Insured Payment Statistics (exclude POLICYHOLDER) */}
                <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-lg p-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600">Paid Fully</label>
                    <p className="text-sm text-green-600 font-bold">
                      {participantsWithPayments.filter(p => p.role !== 'POLICYHOLDER' && p.dueCents === 0 && p.paidCents > 0).length} insured{participantsWithPayments.filter(p => p.role !== 'POLICYHOLDER' && p.dueCents === 0 && p.paidCents > 0).length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">Paid Partially</label>
                    <p className="text-sm text-yellow-600 font-bold">
                      {participantsWithPayments.filter(p => p.role !== 'POLICYHOLDER' && p.paidCents > 0 && p.dueCents > 0).length} insured{participantsWithPayments.filter(p => p.role !== 'POLICYHOLDER' && p.paidCents > 0 && p.dueCents > 0).length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">Not Paid</label>
                    <p className="text-sm text-red-600 font-bold">
                      {participantsWithPayments.filter(p => p.role !== 'POLICYHOLDER' && p.paidCents === 0).length} insured{participantsWithPayments.filter(p => p.role !== 'POLICYHOLDER' && p.paidCents === 0).length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">Total Insureds</label>
                    <p className="text-sm text-gray-900 font-bold">
                      {participantsWithPayments.filter(p => p.role !== 'POLICYHOLDER').length} insured{participantsWithPayments.filter(p => p.role !== 'POLICYHOLDER').length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {quote.notes && (
              <div className="pt-4 border-t">
                <label className="text-sm font-medium text-gray-700">Notes</label>
                <p className="text-sm text-gray-900 mt-1">{quote.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Coverage Details (includes Organization and Timeline) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon size={20} />
              Coverage Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Coverage Period */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Coverage Start</label>
                <p className="text-sm text-gray-900">{formatDate(quote.coverageStartDate)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Coverage End</label>
                <p className="text-sm text-gray-900">{formatDate(quote.coverageEndDate)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Cutoff Date</label>
                <p className="text-sm text-gray-900 font-semibold">{getCutoffDate()}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Duration</label>
                <p className="text-sm text-gray-900">{getCoverageDuration()}</p>
              </div>
            </div>
            
            {/* Covered Assets */}
            {quote.underlying_assets?.assets && quote.underlying_assets.assets.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-700">Covered Assets</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {quote.underlying_assets.assets.map((asset, index) => (
                    <span key={index} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                      {asset}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Coverage Items */}
            {quote.underlying_assets?.items && quote.underlying_assets.items.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-700">Coverage Items</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {quote.underlying_assets.items.map((item, index) => (
                    <span key={index} className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Perils Summary */}
            {quote.perils_config?.perils && quote.perils_config.perils.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-700">Covered Perils</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {quote.perils_config.perils.map((peril, index) => (
                    <span key={index} className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                      {peril.peril_id} ({peril.weight}%)
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Organization */}
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 mb-2">
                <BuildingIcon size={16} className="text-gray-600" />
                <label className="text-sm font-medium text-gray-700">Organization</label>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-900">{quote.organization?.name}</p>
                <p className="text-xs text-gray-600">Code: {quote.organization?.code}</p>
              </div>
            </div>

            {/* Timeline */}
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 mb-2">
                <CalendarIcon size={16} className="text-gray-600" />
                <label className="text-sm font-medium text-gray-700">Timeline</label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600">Created</label>
                  <p className="text-sm text-gray-900">{formatDate(quote.createdAt)}</p>
                  {quote.createdByUser && (
                    <p className="text-xs text-gray-500">by {quote.createdByUser.name}</p>
                  )}
                </div>
                {quote.updatedAt !== quote.createdAt && (
                  <div>
                    <label className="text-xs font-medium text-gray-600">Last Updated</label>
                    <p className="text-sm text-gray-900">{formatDate(quote.updatedAt)}</p>
                  </div>
                )}
                {quote.approvedAt && (
                  <div>
                    <label className="text-xs font-medium text-gray-600">Approved</label>
                    <p className="text-sm text-gray-900">{formatDate(quote.approvedAt)}</p>
                    {quote.approvedByUser && (
                      <p className="text-xs text-gray-500">by {quote.approvedByUser.name}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

          </CardContent>
        </Card>
      </div>

      {/* Perils & Coverage Levels */}
      {quote.protectionLevels && Object.keys(quote.protectionLevels).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldIcon size={20} />
              Perils &amp; Coverage Levels
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200 rounded">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Peril</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Strike Return Period</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Exhaust Return Period</th>
                    {!quote.isMasterPolicy && (
                      <>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Strike Threshold</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Exhaust Threshold</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Object.entries(quote.protectionLevels).map(([perilId, level]) => {
                    const pl = level as { strike_level?: number; exhaust_level?: number; strike_value?: number; exhaust_value?: number };
                    const participantAggregate = !quote.isMasterPolicy && aggregates?.participants?.[0];
                    const strikeThreshold = participantAggregate
                      ? (participantAggregate as { strike_threshold?: number }).strike_threshold
                      : aggregates?.total?.strike_threshold;
                    const exhaustThreshold = participantAggregate
                      ? (participantAggregate as { exhaust_threshold?: number }).exhaust_threshold
                      : aggregates?.total?.exhaust_threshold;
                    return (
                      <tr key={perilId}>
                        <td className="px-4 py-2 font-medium text-gray-900 capitalize">{perilId}</td>
                        <td className="px-4 py-2 text-right">{pl.strike_level ?? pl.strike_value ?? '-'} years</td>
                        <td className="px-4 py-2 text-right">{pl.exhaust_level ?? pl.exhaust_value ?? '-'} years</td>
                        {!quote.isMasterPolicy && (
                          <>
                            <td className="px-4 py-2 text-right">{strikeThreshold != null ? `${Number(strikeThreshold).toFixed(1)} mm` : '-'}</td>
                            <td className="px-4 py-2 text-right">{exhaustThreshold != null ? `${Number(exhaustThreshold).toFixed(1)} mm` : '-'}</td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Participants and Payment Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DollarSignIcon size={20} />
              Participants & Payment Status
            </CardTitle>
            {paymentsLoading && (
              <span className="text-sm text-gray-500">Loading payments...</span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            data={participantsWithPayments}
            columns={participantColumns}
            enableSorting={true}
            enableResizing={true}
          />
        </CardContent>
      </Card>

      {/* Perils Configuration (if any) */}
      {quote.perils_config?.perils && quote.perils_config.perils.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangleIcon size={20} />
              Perils Configuration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {quote.perils_config.perils.map((peril, index) => (
                <div key={index} className="border rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-gray-900 capitalize">{peril.peril_id}</h4>
                    <span className="text-sm text-gray-600">{peril.weight}% weight</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                    <div>
                      <span className="text-gray-500">Model:</span> {peril.payout_model}
                    </div>
                    <div>
                      <span className="text-gray-500">Provider:</span> {peril.data_source?.provider}
                    </div>
                    <div>
                      <span className="text-gray-500">Resolution:</span> {peril.data_source?.resolution}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
